import React from 'react';

import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';

import Table from '../../../../Table';
import CreateInvoice from './CreateInvoice';
import CreatePayoffInvoice from './CreatePayoffInvoice';
import ActionsMenu from '../../../../ActionsMenu';

import { formatCurrency } from '../../../../../helpers/currencyMask';
import LoanService from '../../../../../services/LoanService';
import createInvoiceScheduleTableCell from '../../../../../helpers/tables/createInvoiceScheduleTableCell';
import { useServicerStore } from '../../../../../store';
import useQueryParam from '../../../../../hooks/useQueryParam';
import useScheduleActions from './hooks/useScheduleActions';

export default function Schedule({ isFetching, loanId, readOnly, loan }) {
  const { t } = useTranslation();

  const userRole = useServicerStore((state) => state.userRole);
  const isBorrower = userRole === 'borrower';

  const cols = [
    {
      title: t('common.tables.columns.Date'),
      keys: ['date'],
      format: (value) => moment(value).format('M/D/YY'),
    },
    {
      title: t('common.tables.columns.Number'),
      keys: ['pixel_invoice_number'],
      link: (row) => `/loans/${row.loan_id}/invoices/${row.id}?tab=Invoice`,
    },
    {
      title: t('common.tables.columns.Amount'),
      keys: isBorrower ? ['_amount.invoice_total'] : ['_total.invoice_total'],
      type: 'currency',
    },
    {
      title: t('common.tables.columns.Balance'),
      keys: ['balance'],
      format: (_, row) => {
        const total = isBorrower
          ? row._amount?.invoice_total
          : row._total?.invoice_total;

        const invoiceTotal = parseFloat(total ?? 0);
        const amountPaid = parseFloat(row._paid?.amount_paid ?? 0);
        return `$${formatCurrency((invoiceTotal - amountPaid).toFixed(2))}`;
      },
    },
    {
      title: t('common.tables.columns.Status'),
      keys: ['status'],
      type: 'badge',
      badgeColor: (value) => {
        switch (value) {
          case 'paid':
            return 'emerald';
          case 'open':
          case 'due':
            return 'blue';
          case 'past due':
            return 'rose';
          case 'scheduled':
          case 'pending':
            return 'amber';
          default:
            return 'slate';
        }
      },
    },
    {
      title: t('common.tables.columns.Schedule'),
      keys: ['scheduled_payments'],
      format: (value, row) => {
        if (row.date_paid) {
          return `Paid: ${moment(row.date_paid).format('M/D/YY')}`;
        }

        return createInvoiceScheduleTableCell(value);
      },
      tooltip: (row) => {
        return row.status === 'paid'
          ? `Amount Paid: $${formatCurrency(row._paid?.amount_paid)}`
          : null;
      },
    },
    {
      title: t('common.tables.columns.Principal Balance'),
      keys: ['principal_balance_after'],
      type: 'currency',
      headerTooltip: () =>
        t(
          'AddOrEditPage.AddOrEditLoan.Sections.Schedule.The principal balance after principal is paid on this invoice.'
        ),
      tooltipPosition: 'left',
    },
  ];

  const {
    data: schedule,
    isFetching: isScheduleFetching,
    refetch,
  } = useQuery({
    queryKey: ['schedule', loanId],
    queryFn: () => LoanService.getLoanPaymentSchedule({ loanId }),
    enabled: !!loanId,
  });

  const { getQueryParam, addQueryParam } = useQueryParam('addNew');
  const {
    getQueryParam: payoffInvoiceQueryParam,
    addQueryParam: addNewPayoffInvoiceQueryParam,
  } = useQueryParam('addNewPayoffInvoice');
  const isAddNew = getQueryParam();
  const isAddNewPayoffInvoice = payoffInvoiceQueryParam();

  const { actions, isLoading } = useScheduleActions({
    schedule,
    isScheduleFetching,
    loanId,
    refetch,
    readOnly,
    addQueryParam,
    addNewPayoffInvoiceQueryParam,
  });

  if (isAddNewPayoffInvoice) {
    return (
      <CreatePayoffInvoice
        refetch={refetch}
        loanId={loanId}
        lienHolder={loan?.lien_holder_id}
      />
    );
  }

  return (
    <div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <div className="flex justify-between">
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
            Payment Schedule
          </h2>
          {!isAddNew && !isAddNewPayoffInvoice && (
            <div className="h-8 flex">
              <ActionsMenu actions={actions} isLoading={isLoading} />
            </div>
          )}
        </div>
        {!isBorrower && !readOnly && isAddNew && (
          <CreateInvoice refetch={refetch} loanId={loanId} />
        )}
        {!isAddNew && (
          <Table
            cols={cols}
            rows={isBorrower ? schedule : schedule?._invoices}
            isFetching={isFetching}
            minHeight={565}
          />
        )}
      </div>
    </div>
  );
}
